import { AxiosInstance, AxiosRequestConfig } from 'axios';

import { Profile } from '../../types';
import { PaginationRequest } from '../../types/PaginationRequest';
import { SortRequest } from '../../types/SortRequest';
import { userEndpoints } from '../../utils/constants';
import { axios } from '../axiosInstance';

import { UpdateUserBalanceInput, UserResponse, UsersResponse } from './UsersApi.schema';

class UsersApi {
  #requestInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.#requestInstance = axiosInstance;
  }

  getUsers = async (): Promise<UserResponse[]> => this.#requestInstance.get(userEndpoints.user);

  postUsers = async (): Promise<UserResponse[]> => this.#requestInstance.post(userEndpoints.user);

  getUser = async (id: number): Promise<UserResponse> =>
    this.#requestInstance.get(userEndpoints.getUserById(id));

  getMe = async (): Promise<Profile> => this.#requestInstance.get(userEndpoints.me);

  logOut = async (config: AxiosRequestConfig): Promise<void> =>
    this.#requestInstance.get(userEndpoints.logout, config);

  createUser = async (userData: Profile): Promise<UserResponse> =>
    this.#requestInstance.post(userEndpoints.user, userData);

  updateUser = async (input: {
    id: number;
    userData: Pick<Profile, 'phone' | 'birthDate' | 'email' | 'hireDate'> & { sizeId?: number };
  }): Promise<void> =>
    this.#requestInstance.patch(userEndpoints.updateUserById(input.id), input.userData);

  updateUserByAdmin = async (input: {
    id: number;
    userData: Pick<Profile, 'phone' | 'birthDate' | 'email' | 'hireDate' | 'balance' | 'role'> & {
      adminId: number;
    };
  }): Promise<void> =>
    this.#requestInstance.patch(userEndpoints.updateUserByIdByAdmin(input.id), input.userData);

  updateUserBalance = async (input: {
    id: number;
    balanceData: UpdateUserBalanceInput;
  }): Promise<void> =>
    this.#requestInstance.patch(userEndpoints.getUserBalanceById(input.id), input.balanceData);

  getPaginatedList = async (
    data: PaginationRequest & SortRequest<string>
  ): Promise<UsersResponse> => {
    return await this.#requestInstance.get(userEndpoints.user, { params: data });
  };

  getPaginatedFiredUsers = async (
    data: PaginationRequest & SortRequest<string>
  ): Promise<UsersResponse> => {
    return await this.#requestInstance.get(userEndpoints.firedUsers, { params: data });
  };

  getPaginatedDeletedUsers = async (
    data: PaginationRequest & SortRequest<string>
  ): Promise<UsersResponse> => {
    return await this.#requestInstance.get(userEndpoints.deletedUsers, { params: data });
  };

  onFire = async (id: number, isFired: boolean): Promise<void> => {
    return this.#requestInstance.patch(userEndpoints.fireUserById(id), { isFired });
  };

  onDelete = async (id: number, isDeleted: boolean): Promise<void> => {
    return this.#requestInstance.patch(userEndpoints.deleteUserById(id), { isDeleted });
  };

  loginUser = async (): Promise<void> => this.#requestInstance.get(userEndpoints.getUserLogin());
}

export const usersApi = new UsersApi(axios);
